import { useEffect, useState } from 'react';

import { CardClient } from '@atlaskit/link-provider';

import { isLoomLink } from '../../utils/loom-urls';

const isEmpty = (s: string | undefined) => !s || s.trim() === '';

type SmartCardCoverImageResponse = {
	data?: {
		image?: { url?: string };
	};
};

/**
 * When imageSrc is empty and link is a Loom URL, fetches the Loom thumbnail via
 * smartlinks and returns it for display only (not stored in ADF).
 * Otherwise returns imageSrc. Empty string is treated as no image (never returned).
 *
 * imageSrc is set to '' only when: (1) user selects a Loom link and we intentionally
 * do not store the expiring CDN thumbnail, or (2) user selects any link with no cover
 * image. In both cases we avoid rendering a broken image; for Loom we show the
 * resolved thumbnail at render time.
 */
export const useLoomThumbnailUrl = (
	link: string | undefined,
	imageSrc: string | undefined,
): string | undefined => {
	const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(undefined);

	const shouldRefetch = isEmpty(imageSrc) && !!link && isLoomLink(link);

	useEffect(() => {
		if (!shouldRefetch || !link) {
			setResolvedUrl(undefined);
			return;
		}

		let cancelled = false;
		const client = new CardClient();

		client
			.fetchData(link)
			.then((data) => {
				if (cancelled) return;
				const url = (data as SmartCardCoverImageResponse)?.data?.image?.url;
				if (url) {
					setResolvedUrl(url);
				}
			})
			.catch(() => {
				// Ignore fetch errors; component will show no image
			});

		return () => {
			cancelled = true;
		};
	}, [link, shouldRefetch]);

	if (!isEmpty(imageSrc)) {
		return imageSrc;
	}
	if (shouldRefetch) {
		return resolvedUrl;
	}

	return undefined;
};
